import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { Checkbox, Form, Input, Popover, Tag } from 'antd';
import { ChevronDown, ChevronUp, Information } from '../../assets/icons';
import FloatingLabel from '../../components/FloatingLabel/FloatingLabel';
import useShareCountValidation from '../../Hooks/useShareCountValidation';
import { getUsCurrencyFormat } from '../../utils/usCurrencyFormatter';
import { TOOLTIP_TEXT, TRANSACTION_VALIDATION_MESSAGES } from '../../utils/constant';

const SellOrder = React.forwardRef((props, ref) => {
  const {
    setNumberOfShare,
    userPurchasedShare,
    remainingOpenShares,
    numberOfShare,
    limitPrice,
    setLimitPrice,
    highestBid,
    quickBuyPropertyData,
    totalInvestment,
    transactionFee,
    totalPurchase,
    partialFills,
    setPartialFills,
    isBuyOrdersPending,
    minimumPrice,
    rawTotalPurchase,
  } = props;

  const availableToSell = Math.max(0, Number(userPurchasedShare || 0) - (isBuyOrdersPending ? 0 : Number(remainingOpenShares || 0)));
  const [expandAdvanced, setExpandAdvanced] = useState(true);
  const [isTextSelected, setIsTextSelected] = useState(false);
  const { isBelowMinShares, onShareInputChange } = useShareCountValidation(numberOfShare);
  return (
    <div className='buy-content'>
      <Form ref={ref} form={ref?.current}>
        {/* === No Shares Notice === */}
        {userPurchasedShare <= 0 ? (
          <div className='py-40 no-share'>
            <p className='fz-14 text-center mb-8'>No shares to sell</p>
            <p className='fz-12 text-taupe-80'>
              You currently do not own any shares of this property. If you would like to participate on the{' '}
              <Link to='/market/secondary'>Realbricks Market</Link>, please consider purchasing shares in the Buy tab.
            </p>
          </div>
        ) : (
          <>
            <FloatingLabel label='Number of shares' inputId='shares' inputValue={numberOfShare}>
              <Form.Item
                className='mb-12 mt-42'
                validateStatus={(isBelowMinShares || Number(numberOfShare) > availableToSell || Number(numberOfShare) % 1 !== 0) && 'error'}
                help={
                  Number(numberOfShare) % 1 !== 0
                    ? 'Decimal numbers are not allowed.'
                    : isBelowMinShares
                    ? TRANSACTION_VALIDATION_MESSAGES.INVALID_SHARE_NUMBER_SELL
                    : Number(numberOfShare) > availableToSell && `You have exceeded the sell out limit.(i.e more than ${availableToSell} shares)`
                }
              >
                <Input
                  type='number'
                  onWheel={(e) => {
                    e.target.blur();
                  }}
                  controls={false}
                  value={numberOfShare}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setNumberOfShare(value);
                    onShareInputChange(value);
                  }}
                  onSelect={(e) => {
                    window.getSelection().toString().length > 0 ? setIsTextSelected(true) : setIsTextSelected(false);
                  }}
                  onKeyPress={(event) => {
                    if ((!/[0-9]/.test(event.key) || Number(numberOfShare) >= Number(availableToSell)) && !isTextSelected) {
                      event.preventDefault();
                    }
                  }}
                  id='share'
                />
              </Form.Item>
            </FloatingLabel>
            <div className='mb-20 buy-tags'>
              <Tag
                className={numberOfShare?.toString() === availableToSell.toString() && 'ant-tag__selected'}
                onClick={() => setNumberOfShare(availableToSell.toString() || '0')}
              >
                {`All: ${availableToSell}`}
              </Tag>
            </div>
            <FloatingLabel label='Limit price' inputId='shares' inputValue={limitPrice}>
              <Form.Item
                className='mb-12'
                validateStatus={
                  (limitPrice && !/^(?!0\d)(\d+(?:\.\d{1,2})?|\.\d{1,2})$/.test(limitPrice)) ||
                  (limitPrice && Number(limitPrice) < 0.01) ||
                  (limitPrice && Number(limitPrice) > 10000) ||
                  (minimumPrice && limitPrice && Number(limitPrice) < Number(minimumPrice))
                    ? 'error'
                    : ''
                }
                help={
                  limitPrice && !/^(?!0\d)(\d+(?:\.\d{1,2})?|\.\d{1,2})$/.test(limitPrice)
                    ? TRANSACTION_VALIDATION_MESSAGES.INVALID_PRICE_FORMAT
                    : limitPrice && Number(limitPrice) < 0.01
                    ? 'Please note that the minimum payment transfer limit is $0.01. Please adjust the Limit Price to proceed.'
                    : limitPrice && Number(limitPrice) > 10000
                    ? 'Please note that the maximum payment transfer limit is $10,000. Please adjust the Limit Price to proceed.'
                    : minimumPrice && limitPrice && Number(limitPrice) < Number(minimumPrice)
                    ? `The minimum share price for this property is $${Number(minimumPrice).toFixed(2)} per share. Please adjust your price to $${Number(minimumPrice).toFixed(2)} or more to place your sell order.`
                    : ''
                }
              >
                <Input
                  value={limitPrice}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                      setLimitPrice(val);
                    }
                  }}
                  placeholder='$'
                  onKeyPress={(event) => {
                    const val = event.target.value;
                    const dotIndex = val.indexOf('.');
                    if (
                      !/[0-9.]/.test(event.key) ||
                      (event.key === '.' && val.includes('.')) ||
                      (dotIndex !== -1 && val.slice(dotIndex + 1).length >= 2)
                    ) {
                      event.preventDefault();
                    }
                  }}
                  id='limit'
                />
              </Form.Item>
            </FloatingLabel>
            <div className='mb-20 buy-tags'>
              <Tag
                className={
                  Number(highestBid) <= 0 ? 'ant-tag-disabled' : limitPrice?.toString() === highestBid?.toString() && 'ant-tag__selected'
                }
                onClick={() => Number(highestBid) > 0 && setLimitPrice(highestBid?.toString() || '0')}
              >
                {`Best bid: $${getUsCurrencyFormat(highestBid || 0)} `}
              </Tag>
              <Tag
                className={limitPrice?.toString() === quickBuyPropertyData?.lastTradedPrice?.toString() && 'ant-tag__selected'}
                onClick={() => setLimitPrice(quickBuyPropertyData?.lastTradedPrice?.toString() || '0')}
              >
                {`Last price: $${getUsCurrencyFormat(quickBuyPropertyData?.lastTradedPrice || 0)}`}
              </Tag>
            </div>
            {/* Duration dropdown commented out - default value 'Good Until Cancel' is sent in payload */}
            {/* <FloatingLabel label='Duration' inputId='duration' inputValue={duration}>
              <>
                <div id='duration2-anchor' />
                <Form.Item className='mb-12' name='duration'>
                  <Select
                    id='duration'
                    popupClassName='filter-select-dropdown'
                    onChange={(value) => setDuration(value)}
                    value={duration}
                    defaultValue={duration}
                    getPopupContainer={() => document.getElementById('duration2-anchor')}
                    onDropdownVisibleChange={() => setArrowUpDuration2(!arrowUpDuration2)}
                    suffixIcon={arrowUpDuration2 ? <ChevronUp color='#1A1519' /> : <ChevronDown color='#1A1519' />}
                  >
                    <Select.Option value='Good Until Cancel'>Good until cancel</Select.Option>
                    <Select.Option value='Good For The Day'>Good for the day</Select.Option>
                  </Select>
                </Form.Item>
              </>
            </FloatingLabel> */}
            <div className='mb-40 basic-info'>
              <div className='basic-info__item'>
                <span className='basic-info__item__name'>Estimated Credit</span>
                <span className='basic-info__item__value fw-600'>{`$${getUsCurrencyFormat(totalInvestment || 0)}`}</span>
              </div>
              <div className='basic-info__item'>
                <span className='basic-info__item__name'>Platform fee</span>
                <span className='basic-info__item__value'> $2.50</span>
              </div>
              <div className='basic-info__item'>
                <span className='basic-info__item__name'>Broker-Dealer/ATS Fee (1%)</span>
                <span className='basic-info__item__value'> {`$${transactionFee || ''}`}</span>
              </div>
              <div className='basic-info__item'>
                <span className='basic-info__item__name underline'>Estimated Net Credit</span>
                <span className='basic-info__item__value'>{`$${totalPurchase || ''}`}</span>
              </div>
              {numberOfShare && limitPrice && Number(rawTotalPurchase) > 0 && Number(rawTotalPurchase) < 10 && (
                <div className='ant-form-item-explain-error mb-12'>{TRANSACTION_VALIDATION_MESSAGES.MIN_AMOUNT}</div>
              )}
              {numberOfShare && limitPrice && Number(rawTotalPurchase) <= 0 && (
                <div className='ant-form-item-explain-error mb-12'>{TRANSACTION_VALIDATION_MESSAGES.MIN_AMOUNT}</div>
              )}
              {numberOfShare && limitPrice && Number(rawTotalPurchase) > 10000 && (
                <div className='ant-form-item-explain-error mb-12'>{TRANSACTION_VALIDATION_MESSAGES.MAX_AMOUNT}</div>
              )}
            </div>
            <div className='mb-40 mt-20 fz-16'>
              <p className='fw-600 mb-8 cursor-pointer' onClick={() => setExpandAdvanced(!expandAdvanced)}>
                Advanced {expandAdvanced ? <ChevronUp verticalAlign='-7px' /> : <ChevronDown verticalAlign='-7px' />}
              </p>
              {!!expandAdvanced && (
                <div className='options'>
                  <Checkbox checked={partialFills} onChange={(e) => setPartialFills(e.target.checked)} className='fz-16'>
                    Accept partial fills
                    <Popover
                      placement='bottom'
                      popupClassName='div-yeild-pop'
                      content={TOOLTIP_TEXT.PARTIAL_FILLS}
                      trigger='hover'
                    >
                      <span>
                        <Information className='cursor-pointer' verticalAlign='sub' marginLeft='4px' color='#1A1519' />
                      </span>
                    </Popover>
                  </Checkbox>
                </div>
              )}
            </div>
          </>
        )}
      </Form>
    </div>
  );
});

export default SellOrder;
